import { useEffect } from "react";
import { trackCTAClick, trackEvent } from "@/lib/analytics";

/**
 * Global delegated click listener that auto-fires GA4 events for:
 *
 *   1. CTA clicks → any link/button pointing to /apply, /apply#lead-form,
 *      or /book (the canonical "Book a Strategy Call" destinations).
 *      Fires `cta_click` with cta_label = visible text.
 *
 *   2. Internal city / guide / cluster links → any anchor whose href matches
 *      the SEO topic-cluster patterns (b2b-lead-generation/<city>, /linkedin-*,
 *      /seo-*, /paid-ads, /website-design, etc.).
 *      Fires `internal_link_click` with link_url, link_label, link_category.
 *
 * This avoids needing to instrument 30+ individual CTA/link components.
 * Mounted once at the App root.
 */

// City pages live under this prefix.
const CITY_PREFIX = "/b2b-lead-generation/";

// Guide / pillar pages worth measuring as part of internal linking.
const GUIDE_PATHS = new Set<string>([
  "/b2b-lead-generation",
  "/b2b-lead-generation-cost",
  "/b2b-lead-generation-vs-in-house",
  "/linkedin-lead-generation",
  "/linkedin-sdr",
  "/paid-ads",
  "/website-design",
  "/seo-local-search",
  "/demand-generation",
  "/account-based-marketing",
  "/pipeline-acceleration",
  "/enterprise",
]);

// CTA destinations that should always fire `cta_click`.
const CTA_PATHS = new Set<string>(["/apply", "/book", "/start"]);

function classify(pathname: string): "cta" | "city" | "guide" | null {
  if (CTA_PATHS.has(pathname)) return "cta";
  if (pathname.startsWith(CITY_PREFIX)) return "city";
  if (GUIDE_PATHS.has(pathname)) return "guide";
  return null;
}

const GlobalLinkTracker = () => {
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      // Walk up from the click target to find the nearest <a>.
      const target = (e.target as HTMLElement | null)?.closest?.("a");
      if (!target) return;

      const href = target.getAttribute("href");
      if (!href) return;

      // Resolve to a pathname so we treat router Links and plain <a> the same.
      let pathname = href;
      try {
        const url = new URL(href, window.location.origin);
        // Ignore external links — only measure internal navigation.
        if (url.origin !== window.location.origin) return;
        pathname = url.pathname;
      } catch {
        // Relative href: take everything before the hash/query.
        pathname = href.split("#")[0].split("?")[0] || pathname;
      }

      const kind = classify(pathname);
      if (!kind) return;

      const label =
        target.getAttribute("aria-label") ||
        (target.textContent || "").trim().slice(0, 80) ||
        pathname;

      const sourcePage = window.location.pathname;

      if (kind === "cta") {
        trackCTAClick(label || "Book a Strategy Call", sourcePage);
        return;
      }

      trackEvent("internal_link_click", {
        link_label: label,
        link_url: pathname,
        link_category: kind, // "city" | "guide"
        source_page: sourcePage,
      });
    };

    // Capture phase so we get the click even if a child handler stops propagation.
    document.addEventListener("click", handler, { capture: true });
    return () => document.removeEventListener("click", handler, { capture: true });
  }, []);

  return null;
};

export default GlobalLinkTracker;
